import {
  ActionIcon,
  Button,
  Group,
  NativeSelect,
  NumberInput,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { hasAnyTranslation, toLocalized, type LocalizedString } from '../api/localized-string';
import type { AnswerSpec, QuestionSpec } from '../api/workflows';
import { LocalizedStringInput } from './localized-string-input';

/**
 * Shared editors for the per-question authoring UI. Used by both the
 * workflow editor (inside the `collect_questions` step side panel) and
 * the standalone question-set edit page. Kept here so the two surfaces
 * stay visually + behaviourally identical without duplicating the
 * per-kind answer-spec controls.
 *
 * Each exported component takes `(value, onChange, ...)` so callers
 * own the question/answer state. Helpers like `emptyAnswerOfType` are
 * exported alongside so callers can render new rows consistently.
 */

export function emptyAnswerOfType(type: AnswerSpec['type']): AnswerSpec {
  switch (type) {
    case 'free_text':
      return { type: 'free_text' };
    case 'single_choice':
      return {
        type: 'single_choice',
        options: [
          { value: 'option_1', label: 'Option 1' },
          { value: 'option_2', label: 'Option 2' },
        ],
      };
    case 'multi_choice':
      return {
        type: 'multi_choice',
        options: [
          { value: 'option_1', label: 'Option 1' },
          { value: 'option_2', label: 'Option 2' },
        ],
      };
    case 'boolean':
      return { type: 'boolean' };
    case 'numeric':
      return { type: 'numeric' };
  }
}

export function QuestionEditor({
  question,
  onChange,
  onRemove,
}: {
  question: QuestionSpec;
  onChange: (next: QuestionSpec) => void;
  onRemove: () => void;
}) {
  return (
    <Stack
      gap="xs"
      p="sm"
      style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4 }}
    >
      <Group justify="space-between" align="flex-start">
        <TextInput
          label="Question id"
          description="Referenced by decision steps' input.question_id."
          value={question.id}
          onChange={(e) => onChange({ ...question, id: e.currentTarget.value })}
          style={{ flex: 1 }}
        />
        <ActionIcon variant="subtle" color="red" onClick={onRemove} aria-label="Remove question">
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
      <LocalizedStringInput
        label="Prompt"
        value={toLocalized(question.prompt)}
        onChange={(next) => onChange({ ...question, prompt: next })}
      />
      <LocalizedStringInput
        label="Hint"
        description="Helper text shown below the question in the app. Optional."
        value={toLocalized(question.hint)}
        onChange={(next) => {
          const isEmpty = next.default === '' && !hasAnyTranslation(next);
          onChange({ ...question, hint: isEmpty ? undefined : next });
        }}
      />
      <Switch
        label="Required"
        checked={question.required ?? false}
        onChange={(e) => onChange({ ...question, required: e.currentTarget.checked })}
      />
      <NativeSelect
        label="Answer type"
        value={question.answer.type}
        onChange={(e) =>
          onChange({
            ...question,
            answer: emptyAnswerOfType(e.currentTarget.value as AnswerSpec['type']),
          })
        }
        data={[
          { value: 'free_text', label: 'Free text' },
          { value: 'single_choice', label: 'Single choice' },
          { value: 'multi_choice', label: 'Multi choice' },
          { value: 'boolean', label: 'Boolean' },
          { value: 'numeric', label: 'Numeric' },
        ]}
      />
      <AnswerSpecEditor
        answer={question.answer}
        onChange={(next) => onChange({ ...question, answer: next })}
      />
    </Stack>
  );
}

export function AnswerSpecEditor({
  answer,
  onChange,
}: {
  answer: AnswerSpec;
  onChange: (next: AnswerSpec) => void;
}) {
  if (answer.type === 'free_text') {
    return (
      <Group grow>
        <NumberInput
          label="Min length"
          value={answer.min_length ?? ''}
          onChange={(v) =>
            onChange({
              ...answer,
              min_length: typeof v === 'number' ? v : undefined,
            })
          }
          min={0}
        />
        <NumberInput
          label="Max length"
          value={answer.max_length ?? ''}
          onChange={(v) =>
            onChange({
              ...answer,
              max_length: typeof v === 'number' ? v : undefined,
            })
          }
          min={1}
        />
      </Group>
    );
  }
  if (answer.type === 'single_choice' || answer.type === 'multi_choice') {
    // Capture into a narrowed local so the closure callbacks below
    // keep the discriminated-union narrowing TS lost across spreads.
    const choice = answer;
    function updateValue(index: number, val: string) {
      const opts = [...choice.options];
      opts[index] = { ...opts[index], value: val };
      onChange({ ...choice, options: opts });
    }
    function updateLabel(index: number, label: LocalizedString) {
      const opts = [...choice.options];
      opts[index] = { ...opts[index], label };
      onChange({ ...choice, options: opts });
    }
    function updateIcon(index: number, icon: string | undefined) {
      const opts = [...choice.options];
      opts[index] = { ...opts[index], icon };
      onChange({ ...choice, options: opts });
    }
    function removeOption(index: number) {
      const opts = [...choice.options];
      opts.splice(index, 1);
      onChange({ ...choice, options: opts });
    }
    function addOption() {
      onChange({
        ...choice,
        options: [
          ...choice.options,
          {
            value: `option_${choice.options.length + 1}`,
            label: `Option ${choice.options.length + 1}`,
          },
        ],
      });
    }
    return (
      <Stack gap="xs">
        {choice.options.map((opt, i) => (
          <Group key={i} gap="xs" align="flex-start" wrap="nowrap">
            <TextInput
              label="Value"
              value={opt.value}
              onChange={(e) => updateValue(i, e.currentTarget.value)}
              size="xs"
              style={{ width: 160, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <LocalizedStringInput
                label="Label"
                value={toLocalized(opt.label)}
                onChange={(next) => updateLabel(i, next)}
                compact
              />
            </div>
            <TextInput
              label="Icon key"
              placeholder="e.g. body_head"
              value={opt.icon ?? ''}
              onChange={(e) => updateIcon(i, e.currentTarget.value || undefined)}
              size="xs"
              style={{ width: 120, flexShrink: 0 }}
            />
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => removeOption(i)}
              aria-label="Remove option"
              mt={22}
            >
              <IconTrash size={14} />
            </ActionIcon>
          </Group>
        ))}
        <Button size="xs" variant="light" leftSection={<IconPlus size={12} />} onClick={addOption}>
          Add option
        </Button>
      </Stack>
    );
  }
  if (answer.type === 'numeric') {
    return (
      <Group grow>
        <NumberInput
          label="Min"
          value={answer.min ?? ''}
          onChange={(v) => onChange({ ...answer, min: typeof v === 'number' ? v : undefined })}
        />
        <NumberInput
          label="Max"
          value={answer.max ?? ''}
          onChange={(v) => onChange({ ...answer, max: typeof v === 'number' ? v : undefined })}
        />
        <Switch
          label="Integer only"
          checked={answer.integer ?? false}
          onChange={(e) => onChange({ ...answer, integer: e.currentTarget.checked })}
        />
      </Group>
    );
  }
  // boolean: nothing to configure
  return null;
}

/**
 * Question-list editor — manages an ordered list of `QuestionSpec`
 * objects. Shared between the workflow editor (inline) and the
 * question-set edit page.
 */
export function QuestionListEditor({
  questions,
  onChange,
}: {
  questions: QuestionSpec[];
  onChange: (next: QuestionSpec[]) => void;
}) {
  function patch(index: number, next: QuestionSpec) {
    const copy = [...questions];
    copy[index] = next;
    onChange(copy);
  }
  function remove(index: number) {
    const copy = [...questions];
    copy.splice(index, 1);
    onChange(copy);
  }
  function add() {
    const id = `q${questions.length + 1}`;
    onChange([
      ...questions,
      { id, prompt: 'New question', required: true, answer: { type: 'free_text' } },
    ]);
  }

  return (
    <Stack gap="md">
      {questions.length === 0 && (
        <Text c="dimmed" size="sm">
          No questions yet.
        </Text>
      )}
      {questions.map((q, i) => (
        <QuestionEditor
          key={i}
          question={q}
          onChange={(next) => patch(i, next)}
          onRemove={() => remove(i)}
        />
      ))}
      <Button leftSection={<IconPlus size={14} />} variant="light" onClick={add}>
        Add question
      </Button>
    </Stack>
  );
}
